/**
 * Mobile User Profile Component for Rider Gateway
 * Displays user information with mobile-optimized menu
 */

import type { FC } from 'react';
import { Avatar, Button, Dropdown, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  IdcardOutlined,
  DownOutlined,
  CrownOutlined,
  TeamOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import {
  logout,
  selectUser,
  selectIsLoading,
  selectUserType,
  selectUserGroups
} from '../../store/slices/authSlice';

const { Text } = Typography;

// Hide name/role text below 480px so the navbar fits. The same info is
// surfaced in the dropdown menu's first item, one tap away.
const UserInfoText = styled.div`
  text-align: left;
  line-height: 1.2;
  max-width: 120px;
  color: #ffffff;
  font-family: 'Geist', system-ui, sans-serif;

  @media (max-width: 480px) {
    display: none;
  }
`;

const UserInfoLine = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Override antd Button defaults so the trigger reads correctly on the
// midnight app bar — translucent pill, no antd-blue focus ring.
const TriggerButton = styled(Button)`
  &.ant-btn {
    height: auto !important;
    min-height: 36px;
    padding: 4px 10px 4px 6px !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(255, 255, 255, 0.10) !important;
    border-radius: 999px !important;
    color: #ffffff !important;
    box-shadow: none !important;
    transition: background 160ms ease, border-color 160ms ease, transform 120ms ease;
  }
  &.ant-btn:hover,
  &.ant-btn:focus {
    background: rgba(255, 255, 255, 0.10) !important;
    border-color: rgba(255, 255, 255, 0.18) !important;
  }
  &.ant-btn:active {
    transform: scale(0.98);
  }
  &.ant-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(192, 223, 22, 0.30) !important;
  }
`;

const MobileUserProfile: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectIsLoading);
  const userType = useAppSelector(selectUserType);
  const userGroups = useAppSelector(selectUserGroups);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleViewAccount = () => {
    navigate('/account');
  };

  if (!user) {
    return null;
  }

  // Get user's initials for avatar
  const getInitials = (email: string) => {
    return email.split('@')[0].charAt(0).toUpperCase();
  };

  // Get user role display and color
  const getUserRoleInfo = (type: string | undefined, groups: string[]) => {
    if (groups.includes('admin')) return { name: 'Admin', color: '#ff4d4f', icon: <CrownOutlined /> };
    if (groups.includes('analyst')) return { name: 'Analyst', color: '#1890ff', icon: <TeamOutlined /> };
    if (groups.includes('support')) return { name: 'Support', color: '#52c41a', icon: <CustomerServiceOutlined /> };
    // Display user type properly
    if (type === 'individual') return { name: 'Individual', color: '#722ed1', icon: <UserOutlined /> };
    if (type === 'fleet') return { name: 'Fleet', color: '#13c2c2', icon: <TeamOutlined /> };
    return { name: 'User', color: '#722ed1', icon: <UserOutlined /> };
  };

  const roleInfo = getUserRoleInfo(userType, userGroups);

  const dropdownItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: '4px' }}>
            <Text strong>{user.email}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {roleInfo.icon}
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {roleInfo.name}
            </Text>
          </div>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'view-account',
      label: 'View account',
      icon: <IdcardOutlined />,
      onClick: handleViewAccount,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: isLoading ? 'Signing Out...' : 'Sign Out',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
      disabled: isLoading,
      danger: true,
    },
  ];

  return (
    <Dropdown
      menu={{ items: dropdownItems }}
      trigger={['click']}
      placement="bottomRight"
      arrow
    >
      <TriggerButton
        type="text"
        disabled={isLoading}
        aria-label={`Account menu, ${user.email}`}
        aria-haspopup="menu"
      >
        <Space align="center" style={{ minWidth: 0, maxWidth: '100%' }} aria-hidden>
          <Avatar
            size={28}
            style={{
              backgroundColor: roleInfo.color,
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            {getInitials(user.email)}
          </Avatar>
          <UserInfoText>
            <UserInfoLine style={{ fontSize: '13px', fontWeight: 500 }}>
              {user.email.split('@')[0]}
            </UserInfoLine>
            <UserInfoLine
              style={{
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: '"Geist Mono", ui-monospace, monospace',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              {roleInfo.name}
            </UserInfoLine>
          </UserInfoText>
          <DownOutlined style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)' }} />
        </Space>
      </TriggerButton>
    </Dropdown>
  );
};

export default MobileUserProfile;
